import SpineWrapper, { ICancelablePromise } from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/index';
import AssetsLoadManager from '@Core/Loader/AssetsLoadManager';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { setContainerScaleToFitSpineSize } from '@Core/Utils/ContainerUtils';
import { getEventWithoutParams } from '@Core/Utils/ModifierUtils';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { Container, Point } from 'pixi.js';
import { CycleStep, ISymbolsHighlightPresenter, WaysCycleStep } from '../SymbolsHighlighter.types';

export class Ways extends GameComponent implements ISymbolsHighlightPresenter {
    public scene: GameScene;
    private waysContainer: Container;
    private readonly waysSpines: SpineWrapper[][] = [];
    private spineKey?: string;
    private cycleDuration: number;
    private static readonly DEFAULT_CYCLE_TIME = 1000;

    constructor(scene: GameScene) {
        super(scene);
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, { class: Ways.name, source: LoggerSource.Game, message: 'Ways constructor' });
        EventsCenter.on(EGameEvents.RESIZE_REELS, this.onReelsResize, this);
    }

    public show(step: WaysCycleStep): ICancelablePromise[] {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const promises: ICancelablePromise[] = [];
        for (let reel = 0; reel < step.selectionMask.length; reel++) {
            for (let row = 0; row < step.selectionMask[reel].length; row++) {
                if (!step.selectionMask[reel][row]) continue;
                const symbol = visibleSymbols[reel][row];
                if (symbol.isBlocked) continue;
                promises.push(symbol.symbolAnimation.waitForAnimationEvent(symbol.getAnimationVariant(ESymbolAnimations.WIN, step), 'complete'));
            }
        }
        this.playAnimations(step.selectionMask, step).forEach((animationPromise) => promises.push(animationPromise));
        EventsCenter.emit(EGameEvents.CYCLE_WAYS, step);
        return promises;
    }

    hide(): void {
        this.hideAnimations();
    }

    getAnimationDuration(): number {
        return this.cycleDuration || Ways.DEFAULT_CYCLE_TIME;
    }

    public onInit(): void {
        this.spineKey = AssetsLoadManager.getSpinesConfiguration().assets.find((spine) => spine.key === 'ways')?.key;
    }

    public onCreate(): void {
        if (!this.spineKey) return;
        const { reelsSpineScale } = this.gameStore.get(EGameStoreKeys.ReelParameters);

        this.waysContainer = new Container({ position: new Point(this.scene.screen.width / 2, this.scene.screen.height / 2) });
        this.waysContainer.scale = reelsSpineScale;
        this.waysContainer.zIndex = ELayeringOrder.WAYS;

        for (let i = 0; i < this.gameStore.get(EGameStoreKeys.NumberOfReels); i++) {
            for (let j = 0; j < this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel); j++) {
                if (!this.waysSpines[i]) this.waysSpines[i] = [];
                const animation = new SpineWrapper(this.scene, { x: 0, y: 0 }, { dataKey: this.spineKey, manuallyScaleOnResize: true });
                animation.spine.visible = false;
                this.waysContainer.addChild(animation.spine);
                this.waysSpines[i][j] = animation;
            }
        }

        this.scene.stage.addChild(this.waysContainer);
        const waysSpine = this.waysSpines[0][0];
        this.cycleDuration = waysSpine.getAnimationDuration(ESymbolAnimations.WIN);
    }

    private playAnimations(positions: boolean[][], step: CycleStep): ICancelablePromise[] {
        const { defaultSymbolContainerPositions } = this.gameStore.get(EGameStoreKeys.ReelParameters);
        let promises: ICancelablePromise[] = [];
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const { baseWidth, baseHeight, width, height } = this.scene.game.gameStore.get(EGameStoreKeys.SymbolParameters);

        positions.forEach((reel, reelIndex) =>
            reel.forEach((canSymbolPlay, symbolIndex) => {
                const reelSymbol = visibleSymbols[reelIndex][symbolIndex];
                if (canSymbolPlay && !reelSymbol.isBlocked) {
                    if (!this.waysSpines?.[reelIndex]?.[symbolIndex]) return;
                    const waysSpine = this.waysSpines[reelIndex][symbolIndex];
                    const animationName = this.getAnimationVariant(ESymbolAnimations.WIN, step, waysSpine);
                    const hasAnimation = waysSpine.spine.skeleton.data.animations.some((animation) => getEventWithoutParams(animation.name) === animationName);
                    if (!hasAnimation) return;
                    promises.push(waysSpine.waitForAnimationEvent(animationName, 'complete'));
                    this.cycleDuration = waysSpine.getAnimationDuration(animationName);
                    waysSpine.spine.visible = true;
                    waysSpine.scale = reelSymbol.scale;
                    waysSpine.eventManager.playEventAnimation(animationName);
                    waysSpine.setPosition(
                        defaultSymbolContainerPositions[reelIndex][symbolIndex].x,
                        defaultSymbolContainerPositions[reelIndex][symbolIndex + 1].y
                    );
                    setContainerScaleToFitSpineSize(waysSpine.spine, waysSpine, { baseWidth, baseHeight, width, height });
                }
            })
        );
        return promises;
    }

    public getAnimationVariant(animation: ESymbolAnimations, { multiplier }: CycleStep, waysSpine: SpineWrapper): string {
        if (multiplier && multiplier > 1) {
            const animationWithMultiplier = waysSpine.eventManager.getAnimationNameForGameEvent(`${animation}_X_${multiplier}`);

            return animationWithMultiplier ?? animation;
        }

        return animation;
    }

    private hideAnimations(): void {
        this.waysSpines.forEach((reel) =>
            reel.forEach((spine) => {
                spine.spine.visible = false;
                spine.stop();
            })
        );
    }

    private onReelsResize(): void {
        const { reelsSpineScale } = this.gameStore.get(EGameStoreKeys.ReelParameters);
        this.waysContainer?.scale.set(reelsSpineScale);
        this.waysContainer?.position.set(this.scene.screen.width / 2, this.scene.screen.height / 2);
    }
}
